import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Checkbox, CheckboxGroup, Loader, Modal } from 'rsuite';
import { ButtonPrimary, SearchInput } from '../../../../../../components';
import { useDebounce } from '../../../../../../hooks';
import { extractApiErrorMessage } from '../../../../../../utils/extractApiErrorMessage';
import { AssignmentModalState } from '../hooks/usePricingScopeController';
import {
  useActiveIpaUsersQuery,
  useAssignedPricingUsersQuery,
  usePricerAssignmentQuery,
  usePricingScopeCategoriesQuery,
} from '../hooks/usePricingScopeQueries';
import { setupIpaPricingResponsibilitiesSchema, SetupIpaPricingResponsibilitiesForm } from '../schema';
import { PricingScopeLevel, PricingScopeUser } from '../types';
import { filterAvailablePricingUsers } from '../utils';
import styles from '../SetupIpaPricingResponsibilities.module.scss';

const EMPTY_FORM: SetupIpaPricingResponsibilitiesForm = {
  cdUsuario: 0,
  userName: '',
  userEmail: '',
  categories: [],
};

type AssignmentModalProps = {
  state: Exclude<AssignmentModalState, null>;
  level: PricingScopeLevel;
  loading: boolean;
  onClose: () => void;
  onSubmit: (values: SetupIpaPricingResponsibilitiesForm) => Promise<void>;
  onEditExisting: (cdUsuario: number, userEmail: string) => Promise<void>;
};

export const AssignmentModal = ({ state, level, loading, onClose, onSubmit, onEditExisting }: AssignmentModalProps) => {
  const isEditing = state.mode === 'edit';
  const [step, setStep] = useState<'user' | 'categories'>(isEditing ? 'categories' : 'user');
  const [userSearch, setUserSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [conflictingUserId, setConflictingUserId] = useState<number | null>(null);
  const debounce = useDebounce(500);

  const form = useForm<SetupIpaPricingResponsibilitiesForm>({
    defaultValues: isEditing
      ? {
          cdUsuario: state.assignment.cdUsuario,
          userName: state.assignment.userName,
          userEmail: state.assignment.userEmail,
          categories: state.assignment.categories,
        }
      : EMPTY_FORM,
    resolver: zodResolver(setupIpaPricingResponsibilitiesSchema),
  });

  const detailQuery = usePricerAssignmentQuery(isEditing ? state.assignment.id : undefined, isEditing);
  const activeUsersQuery = useActiveIpaUsersQuery(!isEditing);
  const assignedUsersQuery = useAssignedPricingUsersQuery(!isEditing);
  const categoriesQuery = usePricingScopeCategoriesQuery(level.level, debouncedCategorySearch);
  const selectedCategories = form.watch('categories');
  const selectedUserId = form.watch('cdUsuario');
  const isDetailLoading = isEditing && detailQuery.isLoading;
  const isDetailError = isEditing && detailQuery.isError;
  const initialSavedCategories = isEditing ? (detailQuery.data?.categories ?? state.assignment.categories) : EMPTY_FORM.categories;

  useEffect(() => {
    if (!detailQuery.data) return;
    form.reset({
      cdUsuario: detailQuery.data.cdUsuario,
      userName: detailQuery.data.userName,
      userEmail: detailQuery.data.userEmail,
      categories: detailQuery.data.categories,
    });
  }, [detailQuery.data, form]);

  const availableUsers = useMemo(
    () => filterAvailablePricingUsers(activeUsersQuery.data ?? [], assignedUsersQuery.data ?? [], userSearch),
    [activeUsersQuery.data, assignedUsersQuery.data, userSearch],
  );

  const categories = useMemo(() => {
    const resultCategories = categoriesQuery.data ?? [];
    const selected = selectedCategories ?? [];
    const resultSet = new Set(resultCategories);
    const selectedSet = new Set(selected);
    const prioritized = isEditing ? initialSavedCategories.filter(category => selectedSet.has(category)) : [];
    const prioritizedSet = new Set(prioritized);
    const orderedResults = resultCategories.filter(category => !prioritizedSet.has(category));
    const selectedMissingFromResults = selected.filter(category => !resultSet.has(category));

    return Array.from(new Set([...prioritized, ...orderedResults, ...selectedMissingFromResults]));
  }, [categoriesQuery.data, initialSavedCategories, isEditing, selectedCategories]);

  const selectUser = (user: PricingScopeUser) => {
    form.reset({ cdUsuario: user.cdUsuario, userName: user.name, userEmail: user.email, categories: [] });
    setSubmissionError(null);
    setStep('categories');
  };

  const handleCategoriesChange = (values: unknown[]) => {
    form.setValue('categories', values as string[], { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
    form.clearErrors('categories');
  };

  const handleCategorySearch = (value: string) => {
    setCategorySearch(value);
    debounce(() => setDebouncedCategorySearch(value));
  };

  const toggleCategory = (category: string) => {
    const categories = form.getValues('categories');
    const nextCategories = categories.includes(category) ? categories.filter(selectedCategory => selectedCategory !== category) : [...categories, category];
    handleCategoriesChange(nextCategories);
  };

  const submit = form.handleSubmit(async values => {
    setSubmissionError(null);
    setConflictingUserId(null);
    try {
      await onSubmit(values);
    } catch (error) {
      if (error?.response?.status === 409) {
        setConflictingUserId(values.cdUsuario);
        setSubmissionError('Este usuário já possui uma atribuição ativa.');
      } else {
        setSubmissionError(extractApiErrorMessage(error) ?? 'Não foi possível salvar a atribuição.');
      }
    }
  });

  const selectedUser = {
    name: form.watch('userName'),
    email: form.watch('userEmail'),
  };

  return (
    <Modal className={styles['assignment-modal']} show onHide={onClose} size="sm" backdrop="static" keyboard={false}>
      <form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title>{isEditing ? `Editar atribuição — ${state.assignment.userName}` : 'Nova atribuição'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {step === 'user' ? (
            <>
              <p>Escolha o usuário que será o responsável pela precificação de um conjunto de categorias.</p>
              <SearchInput
                aria-label="Buscar usuário por nome ou e-mail"
                placeholder="Buscar por nome ou e-mail..."
                value={userSearch}
                onChange={event => setUserSearch(event.target.value)}
              />
              <div className={styles.listbox}>
                {activeUsersQuery.isError || assignedUsersQuery.isError ? (
                  <div className={styles['modal-feedback']}>Não foi possível carregar os usuários.</div>
                ) : activeUsersQuery.isLoading || assignedUsersQuery.isLoading ? (
                  <Loader center content="Carregando usuários..." />
                ) : availableUsers.length > 0 ? (
                  availableUsers.map(user => (
                    <button key={user.cdUsuario} className={styles['user-option']} type="button" onClick={() => selectUser(user)}>
                      <span>{user.name}</span>
                      <small>{user.email}</small>
                    </button>
                  ))
                ) : (
                  <div className={styles['modal-feedback']}>Nenhum usuário disponível.</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={styles['fixed-user']}>
                <strong>{selectedUser.name}</strong>
                <span>{selectedUser.email}</span>
              </div>
              <p>Selecione uma ou mais categorias. A sobreposição entre responsáveis pela precificação é permitida.</p>
              <SearchInput
                aria-label="Buscar categoria"
                placeholder="Buscar categoria..."
                value={categorySearch}
                onChange={event => handleCategorySearch(event.target.value)}
              />

              <Controller
                control={form.control}
                name="categories"
                render={({ field }) => (
                  <div className={`${styles.listbox} ${styles['category-listbox']}`}>
                    {categoriesQuery.isError || isDetailError ? (
                      <div className={styles['modal-feedback']}>Não foi possível carregar as categorias da atribuição.</div>
                    ) : categoriesQuery.isLoading || isDetailLoading ? (
                      <Loader center content="Carregando categorias..." />
                    ) : categories.length > 0 ? (
                      <CheckboxGroup className={styles['category-group']} name={field.name} value={field.value} onChange={handleCategoriesChange}>
                        {categories.map(category => (
                          <Checkbox
                            key={category}
                            className={styles['category-option']}
                            value={category}
                            onClick={event => {
                              if (event.target === event.currentTarget) toggleCategory(category);
                            }}
                          >
                            <span>{category}</span>
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    ) : (
                      <div className={styles['modal-feedback']}>Nenhuma categoria encontrada.</div>
                    )}
                  </div>
                )}
              />

              <p className={styles['selection-count']}>
                {selectedCategories.length === 0
                  ? 'Nenhuma categoria selecionada'
                  : `${selectedCategories.length} ${selectedCategories.length === 1 ? 'categoria selecionada' : 'categorias selecionadas'}`}
              </p>
              {form.formState.errors.categories?.message && <p className={styles.error}>{form.formState.errors.categories.message}</p>}
              {submissionError && (
                <div className={styles['submission-error']}>
                  <p>{submissionError}</p>
                  {conflictingUserId === selectedUserId && (
                    <button type="button" onClick={() => onEditExisting(selectedUserId, selectedUser.email)}>
                      Editar atribuição existente
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <ButtonPrimary size="medium" theme="ghost" type="button" onClick={onClose}>
            cancelar
          </ButtonPrimary>
          <ButtonPrimary size="medium" isLoading={loading} type="submit" disabled={step === 'user' || selectedCategories.length === 0}>
            salvar atribuição
          </ButtonPrimary>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
